import { useEffect, useState } from 'react';
import { superAdminApi } from '../../lib/superAdminApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell, PieChart, Pie, Legend, CartesianGrid, LabelList, LineChart, Line } from 'recharts';
import { Trophy, TrendingDown, AlertTriangle, Loader2, Users, TrendingUp, Camera } from 'lucide-react';
import { toast } from 'sonner';

// SaaS-Hub palette (navy/blue) with semantic accents
const COLORS = ['#2196C9', '#5BB8E8', '#10b981', '#f59e0b', '#a855f7'];
const GRID = 'rgba(255,255,255,0.08)';
const AXIS = 'rgba(255,255,255,0.5)';
const TOOLTIP_STYLE = { background: '#0D1B3E', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9' };

export default function SuperAdminInsights() {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      superAdminApi.insights().then((r) => r.data),
      superAdminApi.timeline({ days: 30 }).then((r) => r.data),
    ]).then(([ins, tl]) => { setData(ins); setTimeline(tl); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const captureNow = async () => {
    setCapturing(true);
    try {
      await superAdminApi.captureSnapshots();
      toast.success('Snapshot capturado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao capturar');
    } finally { setCapturing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#5BB8E8' }} /></div>;
  if (!data || !data.summary) return <div className="text-white/60">Sem dados ainda — provisiona pelo menos 2 tenants.</div>;

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="sa-insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Insights cross-tenant</h1>
          <p className="text-white/60 mt-1">Compara todos os teus tenants para vender consultoria e upgrades premium</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={async () => {
            try {
              const { data } = await superAdminApi.checkAlertsNow();
              if (data.sent) toast.success(`${data.count} alertas enviados para ${data.recipients?.length || 0} super admins`);
              else toast.info('Sem alertas — nenhum tenant abaixo dos thresholds');
            } catch (err) { toast.error(err.response?.data?.detail || 'Falha'); }
          }} variant="outline" className="border-red-500/50 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="sa-check-alerts-btn">
            <AlertTriangle className="w-4 h-4 mr-2" /> Verificar alertas agora
          </Button>
          <Button
            onClick={captureNow}
            disabled={capturing}
            variant="outline"
            className="bg-transparent"
            style={{ borderColor: 'rgba(91,184,232,0.5)', color: '#5BB8E8' }}
            data-testid="sa-capture-snapshot-btn"
          >
            {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-2" /> Capturar snapshot agora</>}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard icon={Users} value={s.total_tenants} label="Total de tenants" iconBg="#2196C9" valueColor="text-white" />
        <SummaryCard icon={Trophy} value={`${s.best_tenant.score}%`} label={<>Melhor: <b className="text-white/90">{s.best_tenant.name}</b></>} iconBg="#10b981" valueColor="text-emerald-400" />
        <SummaryCard icon={TrendingDown} value={`${s.avg_score}%`} label="Score médio" iconBg="#5BB8E8" valueColor="text-[#5BB8E8]" />
        <SummaryCard icon={AlertTriangle} value={s.total_overdue_tasks} label="Tarefas em atraso (todos)" iconBg="#ef4444" valueColor="text-red-400" />
      </div>

      {/* Comparative bar chart */}
      <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white">Score por tenant vs média vs melhor</CardTitle>
          <p className="text-sm text-white/60 mt-1">Identifica os tenants que precisam de intervenção urgente ou upgrade de plano.</p>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.compare} layout="vertical" margin={{ top: 20, right: 40, left: 100, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis type="number" domain={[0, 100]} stroke={AXIS} />
                <YAxis type="category" dataKey="tenant" stroke={AXIS} width={140} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(33,150,201,0.1)' }} />
                <ReferenceLine x={s.avg_score} stroke="#5BB8E8" strokeDasharray="4 2" label={{ value: `Média ${s.avg_score}%`, fill: '#5BB8E8', position: 'insideBottomRight' }} />
                <ReferenceLine x={s.best_tenant.score} stroke="#10b981" strokeDasharray="4 2" label={{ value: 'Melhor', fill: '#10b981', position: 'insideTopRight' }} />
                <Bar dataKey="score" radius={[0, 6, 6, 0]}>
                  {data.compare.map((e, i) => (
                    <Cell key={i} fill={e.score >= 80 ? '#10b981' : e.score >= 60 ? '#5BB8E8' : '#ef4444'} />
                  ))}
                  <LabelList dataKey="score" position="right" fill="#f1f5f9" formatter={(v) => `${v}%`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution histogram */}
        <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
          <CardHeader><CardTitle className="text-white">Distribuição de scores</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="range" stroke={AXIS} />
                  <YAxis allowDecimals={false} stroke={AXIS} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(33,150,201,0.1)' }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {data.distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    <LabelList dataKey="count" position="top" fill="#f1f5f9" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* By compliance mode pie */}
        <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
          <CardHeader><CardTitle className="text-white">Score médio por modo</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.by_compliance_mode} dataKey="tenants" nameKey="compliance_mode" outerRadius={90} label={(e) => `${e.compliance_mode}: ${e.avg_score}%`}>
                    {data.by_compliance_mode.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.7)' }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk aggregate */}
      <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
        <CardHeader><CardTitle className="text-white">Sinais de risco agregados</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-3 gap-6 text-center py-6">
          <div>
            <div className="text-4xl font-bold text-red-400">{s.total_critical_incidents}</div>
            <div className="text-sm text-white/60 mt-1">Incidentes críticos abertos</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-orange-400">{s.total_critical_vulns}</div>
            <div className="text-sm text-white/60 mt-1">Vulnerabilidades críticas</div>
          </div>
          <div>
            <div className="text-4xl font-bold" style={{ color: '#5BB8E8' }}>{s.avg_users_active_30d}</div>
            <div className="text-sm text-white/60 mt-1">Users activos/30d (média)</div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline chart */}
      {timeline && (
        <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5" style={{ color: '#5BB8E8' }} /> Evolução do score (últimos 30 dias)
            </CardTitle>
            <p className="text-sm text-white/60 mt-1">Snapshots diários automáticos às 03:15 UTC. {timeline.raw_snapshots_count} pontos capturados.</p>
          </CardHeader>
          <CardContent>
            {timeline.series.length === 0 ? (
              <div className="p-8 text-center text-white/50 text-sm">
                Ainda sem snapshots. Clica em "Capturar snapshot agora" para o primeiro ponto.
              </div>
            ) : (
              <>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timeline.series}>
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                      <XAxis dataKey="date" stroke={AXIS} />
                      <YAxis domain={[0, 100]} stroke={AXIS} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.7)' }} />
                      {timeline.tenants.map((tname, i) => (
                        <Line key={tname} type="monotone" dataKey={tname} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {timeline.per_tenant_summary?.length > 0 && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {timeline.per_tenant_summary.map((t) => (
                      <div key={t.tenant_id} className="p-3 rounded-lg bg-[#0a0e1a]/60 border border-white/10">
                        <div className="text-xs text-white/60">{t.tenant_name}</div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="text-2xl font-bold text-white">{t.last_score}%</span>
                          <span className={`text-sm font-semibold ${t.delta > 0 ? 'text-emerald-400' : t.delta < 0 ? 'text-red-400' : 'text-white/50'}`}>
                            {t.delta > 0 ? '+' : ''}{t.delta}
                          </span>
                          <span className="text-xs text-white/50">({t.data_points} pts)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(90deg, rgba(33,150,201,0.1), rgba(91,184,232,0.1))', border: '1px solid rgba(91,184,232,0.3)' }}>
        <p className="text-sm" style={{ color: '#5BB8E8' }}>
          <b>💡 Sugestão comercial:</b> tenants abaixo de <b>{s.avg_score}%</b> são candidatos ideais para pacotes de consultoria.
          O tenant <b>"{s.worst_tenant.name}"</b> ({s.worst_tenant.score}%) está mais crítico — considera reach-out proactivo.
        </p>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, value, label, iconBg, valueColor }) {
  return (
    <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm hover:border-white/20 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: iconBg }}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
            <div className="text-xs text-white/50 uppercase tracking-wide truncate">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
